import type { Hall, Perms, Table } from '../types'

/**
 * Masanın tam adı (28 Eki 2026, kullanıcı): salondaki masa "Aile → Masa 1",
 * salonsuz masa yalnız adıyla. Sipariş masayı bu adla tutuyor (table_name),
 * fiş, mutfak ekranı, takip ve rapor da bunu yazıyor; iki salonda aynı adlı
 * masa böylece karışmıyor.
 *
 * Veritabanındaki public.table_label ile birebir aynı olmalı
 * (supabase/salon-masa.sql): masa ya da salon adı değişince açık adisyonları
 * orası yeniden adlandırıyor.
 */
export function tableLabel(table: Pick<Table, 'name' | 'hallId'>, halls: Hall[]): string {
  const hall = table.hallId ? halls.find((h) => h.id === table.hallId) : undefined
  const hallName = hall?.name.trim()
  return hallName ? `${hallName} → ${table.name}` : table.name
}

/**
 * Garsonun baktığı salonlar (perms.halls). Boş ya da yok: hepsi, eskisi
 * gibi. Silinmiş bir salon listede kalmışsa yok sayılır; hepsi silinmişse
 * garson yine bütün salonları görür, boş ekranda kalmaz.
 */
export function allowedHallIds(perms: Perms | undefined, halls: Hall[]): Set<string> | null {
  const picked = (perms?.halls ?? []).filter((id) => halls.some((h) => h.id === id))
  return picked.length > 0 ? new Set(picked) : null
}

/** Garsonun görebileceği masalar. Salonsuz masalar salon seçilmişse görünmez. */
export function visibleTables<T extends Pick<Table, 'hallId'>>(tables: T[], allowed: Set<string> | null): T[] {
  if (!allowed) return tables
  return tables.filter((t) => !!t.hallId && allowed.has(t.hallId))
}

/*
 * Görünen ad (28 Eki 2026, kullanıcı): başka salonda aynı adlı masa yoksa
 * yalnız masa adı ("Masa 2"), varsa salonla ("Salon → Masa 2"). Yalnız
 * görünüş; sipariş masayı yine tam adla tutuyor. Tam adı kaydetmek,
 * ileride aynı adlı ikinci masa açılınca açık adisyonları yeniden adlandırma
 * zorunluluğunu ortadan kaldırıyor.
 *
 * Masa listesi CatalogContext'ten buraya bildiriliyor; fiş uygulamanın en
 * dışında basıldığı için kancasız, düz bir işlev.
 */
let nameCounts = new Map<string, number>()

const norm = (s: string) => s.trim().toLocaleLowerCase('tr-TR')

export function setTableIndex(tables: Pick<Table, 'name' | 'active'>[]): void {
  const next = new Map<string, number>()
  for (const t of tables) {
    if (t.active === false) continue
    const k = norm(t.name)
    next.set(k, (next.get(k) ?? 0) + 1)
  }
  nameCounts = next
}

export function displayTable(label: string): string
export function displayTable(label: string | undefined | null): string | undefined
export function displayTable(label: string | undefined | null): string | undefined {
  if (!label) return label ?? undefined
  const at = label.indexOf(' → ')
  if (at < 0) return label
  const name = label.slice(at + 3)
  // Masa listesi henüz yoksa tam ad: neyin tek olduğu bilinmiyor.
  if (nameCounts.size === 0) return label
  return (nameCounts.get(norm(name)) ?? 0) > 1 ? label : name
}
